import { useMemo, type HTMLAttributes } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { take } from 'lib/array/arrayUtils'
import UserAvatar from 'components/Common/UserAvatar'
import { mediaQueryUp } from 'components/utils/breakpoint'

const CarrierLogoGrid = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: ${rem(12)};
  place-content: center;
  justify-items: center;
  align-items: center;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  grid-template-columns: 1fr;

  &.two-carriers {
    grid-template-columns: repeat(2, auto);
  }

  &.three-carriers {
    grid-template-columns: repeat(3, auto);
  }

  &.more-carriers {
    grid-template-columns: repeat(4, auto);

    ${mediaQueryUp.tablet} {
      grid-template-columns: repeat(2, auto);
    }
  }
`

const CarrierLogo = styled.img`
  width: ${rem(36)};

  &.carrier-logo-lg {
    width: ${rem(115)};
  }
`

const singleLogoClassName = 'carrier-logo-lg'

interface Props extends HTMLAttributes<HTMLDivElement> {
  logos: Array<string>
}

function OrderCarrierLogoHero({ logos, className, ...rest }: Props) {
  const splitCarrierLogos = useMemo(() => take(logos, 3), [logos])
  const moreCarriersCount = logos.length - 3
  const isSingleCarrier = logos.length === 1
  const gridClassName = useMemo(() => clsx(className, {
    'two-carriers': logos.length === 2,
    'three-carriers': logos.length === 3,
    'more-carriers': logos.length > 3,
  }), [className, logos.length])

  return (
    <CarrierLogoGrid className={gridClassName} {...rest}>
      {splitCarrierLogos.map((logo) => (
        <CarrierLogo
          alt=""
          className={isSingleCarrier ? singleLogoClassName : undefined}
          key={logo}
          src={logo}
        />
      ))}
      {moreCarriersCount > 1 && (
        <UserAvatar
          variant="secondary"
          size="large"
          firstName="+"
          lastName={moreCarriersCount.toString()}
        />
      )}
    </CarrierLogoGrid>
  )
}

export default OrderCarrierLogoHero
